import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onIndex, title }) {
  const total = images?.length || 0;

  const next = useCallback(() => onIndex((index + 1) % total), [index, total, onIndex]);
  const prev = useCallback(() => onIndex((index - 1 + total) % total), [index, total, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, next, prev]);

  if (total === 0) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col animate-fade-in-up"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 md:px-10 py-4 md:py-5 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          {title && <div className="font-display text-lg md:text-xl truncate">{title}</div>}
          <div className="text-xs uppercase tracking-widest text-white/60 font-bold">
            {index + 1} / {total}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          className="w-11 h-11 flex items-center justify-center border border-white/20 hover:bg-white hover:text-black transition-colors"
          aria-label="Sluiten"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center px-4 md:px-16 py-4 relative" onClick={(e) => e.stopPropagation()}>
        {total > 1 && (
          <button
            onClick={prev}
            data-testid="lightbox-prev"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/10 hover:bg-white hover:text-black text-white transition-colors backdrop-blur-sm"
            aria-label="Vorige"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img
          src={images[index]}
          alt={`${title || "Project"} — foto ${index + 1}`}
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
        {total > 1 && (
          <button
            onClick={next}
            data-testid="lightbox-next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/10 hover:bg-white hover:text-black text-white transition-colors backdrop-blur-sm"
            aria-label="Volgende"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnails */}
      {total > 1 && (
        <div className="px-4 md:px-10 pb-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center overflow-x-auto py-2">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => onIndex(i)}
                data-testid={`lightbox-thumb-${i}`}
                className={`w-16 h-16 md:w-20 md:h-20 shrink-0 overflow-hidden border-2 transition-all ${
                  i === index ? "border-[#FF5A1F] opacity-100" : "border-white/20 opacity-60 hover:opacity-100"
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
